/**
 * Local data cache using IndexedDB for instant page loads.
 * Strategy: Cache-first, background refresh.
 *
 * Flow:
 * 1. Check IndexedDB for cached data → show instantly
 * 2. Fetch from API in background
 * 3. Compare hash → if changed, update cache + UI
 * 4. If offline, use cached data
 */

const DB_NAME = 'lead-intel-cache';
const DB_VERSION = 1;
const STORE_NAME = 'api-cache';

// ─── IndexedDB helpers ───
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getFromDB(key) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function setInDB(key, data, hash) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ key, data, hash, timestamp: Date.now() });
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}

// Simple hash for comparing data
function quickHash(data) {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < Math.min(str.length, 10000); i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return `${hash}_${str.length}`;
}

// ─── Main cache function for React Query ───

/**
 * Creates a queryFn that:
 * 1. Returns cached data instantly (if available)
 * 2. Fetches fresh data in background
 * 3. Updates cache if data changed
 */
export function createCachedQueryFn(apiCall, cacheKey, maxAgeMs = 5 * 60 * 1000) {
  let hasFetchedFresh = false;

  return async () => {
    // Try to get from IndexedDB first
    const cached = await getFromDB(cacheKey);

    if (cached && (Date.now() - cached.timestamp < maxAgeMs)) {
      // Data is fresh enough, return it and skip network
      return cached.data;
    }

    if (cached && !hasFetchedFresh) {
      // Data exists but stale — return stale data, fetch fresh in background
      hasFetchedFresh = true;
      // Background fetch (don't await)
      apiCall().then(freshData => {
        const newHash = quickHash(freshData);
        if (newHash !== cached.hash) {
          setInDB(cacheKey, freshData, newHash);
        }
      }).catch(() => {});
      return cached.data;
    }

    // No cache or already tried stale — fetch fresh
    try {
      const freshData = await apiCall();
      const hash = quickHash(freshData);
      await setInDB(cacheKey, freshData, hash);
      hasFetchedFresh = true;
      return freshData;
    } catch (err) {
      // If network fails but we have cache, use it
      if (cached) return cached.data;
      throw err;
    }
  };
}

/**
 * Prefetch critical data on login — fills IndexedDB before user navigates.
 */
export async function prefetchCriticalData(api) {
  const endpoints = [
    { key: 'dashboard_HR', url: '/leads/dashboard?country=HR' },
    { key: 'company_statuses', url: '/leads/company-status' },
    { key: 'watchlist', url: '/leads/watchlist' },
    { key: 'reminders', url: '/leads/reminders' },
  ];

  // Fetch all in parallel
  await Promise.allSettled(
    endpoints.map(async ({ key, url }) => {
      try {
        const { data } = await api.get(url);
        await setInDB(key, data, quickHash(data));
      } catch {}
    })
  );
}

/**
 * Clear all cached data (on logout)
 */
export async function clearCache() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
  } catch {}
}

export { getFromDB, setInDB, quickHash };
